// src/pages/CaptionGenerator.js
import React, { useState, useRef } from 'react';
import { generateCaptions, expandCaption, rewriteCaption } from '../services/apiService';
import MiniToolPageLayout from '../components/MiniToolPageLayout';
import { IoChatbubblesOutline, IoCopyOutline, IoSparklesOutline, IoAddOutline, IoTrashOutline } from 'react-icons/io5';
// ✅ RESTORED: Import the hero background image
import heroBg from '../assets/images/caption-generator-background.png';

// --- Reusable Form Components ---
const FormGroup = ({ label, children }) => (
  <div className="flex flex-col gap-2">{label && <label className="text-sm font-medium text-dark-text-secondary">{label}</label>}{children}</div>
);

const SelectField = ({ label, value, onChange, options, disabled }) => (
  <FormGroup label={label}>
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      disabled={disabled}
      className="w-full bg-dark-card/60 border border-white/10 rounded-lg px-3 py-2 text-sm text-dark-text-primary focus:ring-2 focus:ring-dark-accent-start outline-none"
    >
      {options.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
    </select>
  </FormGroup>
);

// --- Main Tool Component ---
const CaptionGeneratorTool = () => {
    const [prompt, setPrompt] = useState("");
    const [platform, setPlatform] = useState("instagram");
    const [tone, setTone] = useState("witty");
    const [captionList, setCaptionList] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [refiningId, setRefiningId] = useState(null); // Tracks which caption is being expanded/rewritten
    const [errorMsg, setErrorMsg] = useState("");
    const genIdRef = useRef(0);
    const nextId = () => `caption-${++genIdRef.current}`;

    const handleGenerate = async (e) => {
        e.preventDefault();
        setErrorMsg("");
        setIsLoading(true);
        const t0 = performance.now();
        try {
            const result = await generateCaptions({ prompt, platform, tone, n: 3 });
            const elapsed = ((performance.now() - t0) / 1000).toFixed(2);
            const newCaptions = result.map(item => ({ id: nextId(), text: item.caption, hashtags: item.hashtags, type: 'original', elapsedSec: elapsed }));
            setCaptionList(prev => [...newCaptions, ...prev]);
        } catch (err) {
            setErrorMsg(err.message || String(err));
        }
        setIsLoading(false);
    };
    
    // ✅ RESTORED: Full implementation for expand/rewrite/remove
    const handleRefine = async (id, action) => {
        setRefiningId(id);
        const originalCaption = captionList.find(c => c.id === id);
        if (!originalCaption) return;

        try {
            const apiCall = action === 'expand' ? expandCaption : rewriteCaption;
            const result = await apiCall(originalCaption.text);
            const newCaption = { id: nextId(), text: result[0].text, hashtags: '', type: action, elapsedSec: '...' };
            setCaptionList(prev => [newCaption, ...prev.filter(c => c.id !== id)]);
        } catch (err) {
            setErrorMsg(`Failed to ${action} caption.`);
        }
        setRefiningId(null);
    };

    const handleRemove = (id) => setCaptionList(prev => prev.filter(c => c.id !== id));

    return (
        <div className="mx-auto max-w-7xl px-6">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                {/* Left Column: Controls */}
                <div className="lg:col-span-1">
                    <form onSubmit={handleGenerate} className="sticky top-24 flex flex-col gap-6 p-6 bg-dark-card/40 backdrop-blur-lg border border-white/10 rounded-2xl">
                        <FormGroup label="Your Topic or Prompt">
                            <textarea
                                rows={4}
                                placeholder="e.g., Launching a new coffee blend..."
                                value={prompt}
                                onChange={(e) => setPrompt(e.target.value)}
                                disabled={isLoading}
                                className="w-full bg-dark-card/60 border border-white/10 rounded-lg px-3 py-2 text-sm text-dark-text-primary focus:ring-2 focus:ring-dark-accent-start outline-none"
                            />
                        </FormGroup>
                        <SelectField label="Platform" value={platform} onChange={setPlatform} disabled={isLoading} options={[
                            { value: 'instagram', label: 'Instagram' }, { value: 'facebook', label: 'Facebook' }, { value: 'twitter', label: 'X (Twitter)' }, { value: 'linkedin', label: 'LinkedIn' }, { value: 'tiktok', label: 'TikTok' }
                        ]}/>
                        <SelectField label="Tone of Voice" value={tone} onChange={setTone} disabled={isLoading} options={[
                            { value: 'witty', label: 'Witty' }, { value: 'professional', label: 'Professional' }, { value: 'inspirational', label: 'Inspirational' }, { value: 'friendly', label: 'Friendly' }, { value: 'bold', label: 'Bold' }
                        ]}/>
                        <button type="submit" disabled={isLoading || !prompt} className="w-full rounded-lg bg-gradient-to-r from-dark-accent-start to-dark-accent-end px-4 py-3 text-sm font-semibold text-white shadow-sm hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed">
                            {isLoading ? 'Generating...' : 'Generate Captions'}
                        </button>
                        {errorMsg && <p className="text-sm text-red-400 mt-2">{errorMsg}</p>}
                    </form>
                </div>
                {/* Right Column: Results */}
                <div className="lg:col-span-2 space-y-4">
                    {captionList.length === 0 && !isLoading && (
                        <div className="flex flex-col items-center justify-center text-center p-8 bg-dark-card/40 border border-white/10 rounded-2xl min-h-[300px]">
                            <IoChatbubblesOutline className="h-12 w-12 text-dark-text-secondary/50 mb-4" />
                            <h3 className="text-lg font-semibold text-dark-text-primary">Your generated captions will appear here</h3>
                            <p className="mt-2 text-sm text-dark-text-secondary">Fill out the form on the left to get started.</p>
                        </div>
                    )}
                    {captionList.map(caption => (
                        <div key={caption.id} className={`bg-dark-card/40 border border-white/10 rounded-2xl p-6 animate-fade-in transition-opacity ${refiningId === caption.id ? 'opacity-50' : 'opacity-100'}`}>
                            <p className="text-dark-text-primary whitespace-pre-wrap">{caption.text}</p>
                            {caption.hashtags && <p className="mt-4 text-sm text-dark-accent-start whitespace-pre-wrap">{caption.hashtags}</p>}
                            <div className="mt-4 pt-4 border-t border-white/10 flex justify-end items-center gap-2 text-xs text-dark-text-secondary">
                                <span className="mr-auto text-dark-text-secondary/60 capitalize">{caption.type}</span>
                                <button onClick={() => navigator.clipboard.writeText(`${caption.text}\n\n${caption.hashtags}`)} title="Copy" className="p-2 rounded-md hover:bg-white/10 hover:text-white"><IoCopyOutline size={16} /></button>
                                <button onClick={() => handleRefine(caption.id, 'expand')} title="Expand" disabled={refiningId} className="p-2 rounded-md hover:bg-white/10 hover:text-white disabled:opacity-50"><IoAddOutline size={16} /></button>
                                <button onClick={() => handleRefine(caption.id, 'rewrite')} title="Rewrite" disabled={refiningId} className="p-2 rounded-md hover:bg-white/10 hover:text-white disabled:opacity-50"><IoSparklesOutline size={16} /></button>
                                <button onClick={() => handleRemove(caption.id)} title="Remove" disabled={refiningId} className="p-2 rounded-md hover:bg-red-500/20 hover:text-red-400"><IoTrashOutline size={16} /></button>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

// --- Page Definition ---
const pageData = {
  seo: {
    title: "Free AI Caption & Hashtag Generator | Oolook Tools",
    description: "Generate engaging, platform-specific captions and relevant hashtags in seconds. Oolook's free AI tool helps you overcome writer's block and create better content.",
    image: 'https://oolook.in/og/caption-generator.png',
    schemas: [{
      "@context": "https://schema.org",
      "@type": "WebApplication",
      "name": "AI Caption Generator",
      "operatingSystem": "All",
      "applicationCategory": "CreativeWork",
      "description": "Generate, rewrite, and expand social media captions and hashtags instantly with Oolook’s free AI tool.",
      "url": "https://oolook.in/caption-generator",
      "offers": { "@type": "Offer", "price": "0", "priceCurrency": "USD" },
    }]
  },
  hero: {
    icon: IoChatbubblesOutline,
    title: "AI Caption Generator",
    subtitle: "Overcome writer's block forever. Generate dozens of engaging, on-brand captions and hashtags from a single prompt.",
    backgroundImage: heroBg // ✅ RESTORED: Background image is used here
  },
  guide: {
    title: "How It Works",
    subtitle: "Perfect captions in three simple steps.",
    steps: [
      { title: "1. Write Your Prompt", description: "Describe your post, topic, or promotion in the prompt box. The more detail you give, the better the results." },
      { title: "2. Select Your Tone & Platform", description: "Choose the social media platform you're writing for and the desired tone of voice to tailor the output." },
      { title: "3. Generate & Refine", description: "Get multiple unique captions in seconds. Copy your favorite, or use the refine tools to expand or rewrite it." }
    ]
  }
};

const CaptionGeneratorPage = () => {
  return (
    <MiniToolPageLayout seo={pageData.seo} hero={pageData.hero} guide={pageData.guide}>
      <CaptionGeneratorTool />
    </MiniToolPageLayout>
  );
};

export default CaptionGeneratorPage;